import { useState, useEffect } from 'react';
import { Eye, Download, Trash, Heart } from 'lucide-react';
import { useFavoritesContext } from '@/contexts/FavoritesContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { showApiError, showApiSuccess } from '@/lib/toast-utils';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Link } from 'wouter';
import { getPhotoUrl } from '@/utils/photoUtils';
import type { Event as AppEvent, Photo } from '@/types/schema';

export default function FavoritesPage() {
  const { favorites, isLoading, error, removeFromFavorites, clearAllFavorites, refreshFavorites } = useFavoritesContext();
  const [isClearing, setIsClearing] = useState(false);

  // Garantir que favorites seja sempre um array
  const safeFavorites = Array.isArray(favorites) ? favorites : [];

  useEffect(() => {
    refreshFavorites();
  }, [refreshFavorites]);

  const handleRemoveFavorite = async (itemId: string, itemType: 'photo' | 'event') => {
    try {
      await removeFromFavorites(itemId, itemType);
      showApiSuccess('Item removido dos favoritos');
    } catch (error) {
      console.error('Erro ao remover favorito:', error);
      showApiError('Erro ao remover favorito');
    }
  };

  const handleClearAllFavorites = async () => {
    try {
      setIsClearing(true);
      await clearAllFavorites();
      showApiSuccess('Todos os favoritos foram removidos');
    } catch (error) {
      console.error('Erro ao limpar favoritos:', error);
      showApiError('Erro ao limpar favoritos');
    } finally {
      setIsClearing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando favoritos...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-red-600 mb-4">Erro ao carregar favoritos: {error}</p>
          <Button onClick={refreshFavorites}>Tentar novamente</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Meus Favoritos</h1>
          <p className="text-gray-600">
            {safeFavorites.length === 0 
              ? 'Você ainda não tem favoritos salvos.' 
              : `${safeFavorites.length} ${safeFavorites.length === 1 ? 'item favoritado' : 'itens favoritados'}`
            }
          </p>
        </div>
        
        {safeFavorites.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" disabled={isClearing}>
                <Trash className="h-4 w-4 mr-2" />
                {isClearing ? 'Limpando...' : 'Limpar Todos'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmar limpeza</AlertDialogTitle>
                <AlertDialogDescription>
                  Tem certeza que deseja remover todos os favoritos? Esta ação não pode ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleClearAllFavorites} className="bg-red-600 hover:bg-red-700">
                  Limpar Todos
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {safeFavorites.length === 0 ? (
        <div className="text-center py-16">
          <Heart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Nenhum favorito ainda</h3>
          <p className="text-gray-600 mb-6">Explore nossos eventos e fotos para adicionar aos seus favoritos!</p>
          <div className="space-x-4">
            <Link to="/">
              <Button>Explorar Fotos</Button>
            </Link>
            <Link to="/events">
              <Button variant="outline">Ver Eventos</Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {safeFavorites.map((favorite, index) => {
            const item = favorite.itemDetails;
            if (!item) return null;

            // Gerar chave única mais robusta
            const uniqueKey = favorite.id || 
                             `${favorite.itemType}-${favorite.itemId}` || 
                             `favorite-${index}-${Date.now()}`;

            return (
              <Card key={uniqueKey} className="group overflow-hidden hover:shadow-lg transition-shadow">
                <CardContent className="p-0">
                  <div className="relative">
                    {/* Image */}
                    <div className="aspect-square overflow-hidden bg-gray-100">
                      {favorite.itemType === 'photo' && favorite.itemDetails ? (
                        <img
                          src={getPhotoUrl((favorite.itemDetails as Photo).filename, 'thumbnail')}
                          alt={`Foto do evento`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            // Fallback para storageUrl se thumbnail falhar
                            const target = e.target as HTMLImageElement;
                            target.src = getPhotoUrl((favorite.itemDetails as Photo).filename, 'storage');
                          }}
                        />
                      ) : favorite.itemType === 'event' && favorite.itemDetails ? (
                        <img
                          src={(favorite.itemDetails as AppEvent).imageUrl || '/placeholder-event.jpg'}
                          alt={(favorite.itemDetails as AppEvent).title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <Heart className="h-12 w-12 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Remove Favorite Button */}
                    <Button
                      size="sm"
                      variant="secondary"
                      className="absolute top-2 right-2 h-8 w-8 p-0 bg-white/90 hover:bg-white shadow-sm"
                      onClick={() => handleRemoveFavorite(favorite.itemId, favorite.itemType)}
                    >
                      <Heart className="h-4 w-4 text-red-500 fill-current" />
                    </Button>

                    {/* Type Badge */}
                    <Badge 
                      className="absolute top-2 left-2 bg-black/70 text-white"
                    >
                      {favorite.itemType === 'photo' ? 'Foto' : 'Evento'}
                    </Badge>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    {favorite.itemType === 'photo' && favorite.itemDetails ? (
                      <>
                        <h3 className="font-semibold text-sm mb-1 line-clamp-2">
                          Foto
                        </h3>
                        {(favorite.itemDetails as Photo).photographerName && (
                          <p className="text-xs text-gray-500 mb-1">
                            Por: {(favorite.itemDetails as Photo).photographerName}
                          </p>
                        )}
                        <p className="text-xs text-gray-600 mb-2">
                          ID: {(favorite.itemDetails as Photo).id.slice(0, 8)}
                        </p>

                        <div className="flex gap-2">
                          <Link to={`/photos/${(favorite.itemDetails as Photo).id}`} className="flex-1">
                            <Button size="sm" variant="outline" className="w-full">
                              <Eye className="h-3 w-3 mr-1" />
                              Ver
                            </Button>
                          </Link>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1"
                            onClick={() => {
                              const photoUrl = getPhotoUrl((favorite.itemDetails as Photo).filename, 'storage');
                              window.open(photoUrl, '_blank');
                            }}
                          >
                            <Download className="h-3 w-3 mr-1" />
                            Download
                          </Button>
                        </div>
                      </>
                    ) : favorite.itemType === 'event' && favorite.itemDetails ? (
                      <>
                        <h3 className="font-semibold text-sm mb-1 line-clamp-2">
                          {(favorite.itemDetails as AppEvent).title}
                        </h3>
                        <p className="text-xs text-gray-600 mb-2">
                          {new Date((favorite.itemDetails as AppEvent).date).toLocaleDateString('pt-BR')}
                        </p>
                        <p className="text-xs text-gray-500 mb-3 line-clamp-2">
                          {(favorite.itemDetails as AppEvent).location}
                        </p>
                        <Link to={`/events/${(favorite.itemDetails as AppEvent).id}`} className="block">
                          <Button size="sm" className="w-full">
                            <Eye className="h-3 w-3 mr-1" />
                            Ver Evento
                          </Button>
                        </Link>
                      </>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}